// src/pages/Permission.js
import React, { useEffect, useState, useMemo, useContext } from 'react';
import {
  Button,
  Container,
  Paper,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { FormProvider } from '../components/FormProvider';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { BASE_URL } from '../utils/BaseUrl';
import { Link } from 'react-router-dom';

// ← import your ColorModeContext
import { ColorModeContext } from "../index";

const styles = {
  formControl: { width: "100%", marginBottom: '16px' },
  table: { width: "100%" }
};

export const Permission = () => {
  // ← consume dark/light mode
  const { mode } = useContext(ColorModeContext);

  const [roles, setRoles] = useState([]);
  const [tabledata, setTabledata] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState({ role: '', parent: '', child: '' });
  const [isChecked, setIsChecked] = useState(false);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const organizationData = useSelector((state) => state.organization);

  const data = useMemo(() => {
    return organizationData.find(item => item.id === 'hospital')?.options || [];
  }, [organizationData]);

  const dataa = useMemo(() => {
    const selectedData = data.find(item => item.id === selectedRoles.parent);
    return selectedData ? selectedData.menu : [];
  }, [data, selectedRoles.parent]);

  const combinedChildData = useMemo(() => {
    const selectedDataa = dataa.find(item => item.id === selectedRoles.child);
    return selectedDataa ? selectedDataa.children || [selectedDataa] : [];
  }, [dataa, selectedRoles.child]);

  useEffect(() => {
    if (combinedChildData.length > 0) {
      setTabledata(combinedChildData);
    } else {
      setTabledata([]);
    }
  }, [combinedChildData]);

  useEffect(() => {
    const fetchRolesData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${BASE_URL}/role/dropdown`);
        setRoles(response.data.data);
      } catch (error) {
        setError('Error fetching roles');
        console.error('Error fetching roles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRolesData();
  }, []);

  useEffect(() => {
    if (permissions["DatePermission"]) {
      setIsChecked(true);
    } else {
      setIsChecked(false);
    }
  }, [permissions]);

  const handleRoleChange = (roleType) => (e) => {
    const roleId = e.target.value;
    setSelectedRoles((prev) => ({ ...prev, [roleType]: roleId }));
    if (roleType === 'role' && roleId) {
      (async () => {
        try {
          const response = await axios.get(`${BASE_URL}/role-master/${roleId}`);
          const rolePermissions = response.data.permissions.reduce((acc, perm) => {
            acc[perm] = true;
            return acc;
          }, {});
          setPermissions(rolePermissions);
        } catch (error) {
          console.error('Error fetching role permissions:', error);
        }
      })();
    }
    if (roleType === 'parent') {
      setSelectedRoles((prev) => ({ ...prev, child: '' }));
      setTabledata([]);
    }
  };

  const handleCheckboxChange = (action, permissionType) => {
    const permissionKey = `${selectedRoles.parent}-${selectedRoles.child}-${action}${permissionType.charAt(0).toUpperCase() + permissionType.slice(1)}`;
    setPermissions((prevPermissions) => ({
      ...prevPermissions,
      [permissionKey]: !prevPermissions[permissionKey]
    }));
  };

  const handleReset = () => {
    setSelectedRoles({ role: '', parent: '', child: '' });
    setTabledata([]);
    setPermissions({});
  };

  const handleSubmitPermissions = async (e) => {
    e.preventDefault();
    if (!selectedRoles.role) {
      alert('Please select a role.');
      return;
    }
    const selectedPermissions = Object.keys(permissions).filter(key => permissions[key]);
    const additionalPermissions = ["Exit", "Submit", "Cancel"];
    let result = [...selectedPermissions, ...additionalPermissions];
    if (isChecked) result.push("DatePermission");
    else result = result.filter(permission => permission !== "DatePermission");

    try {
      await axios.put(`${BASE_URL}/role-master/update/${selectedRoles.role}`, { permissions: result });
      alert("Permissions updated successfully.");
      handleReset();
    } catch (error) {
      console.error('Error updating permissions:', error);
      alert("Error updating permissions.");
    }
  };

  const handleDeletePermissions = async () => {
    if (!selectedRoles.role) {
      alert('Please select a role.');
      return;
    }
    if (window.confirm("Are you sure you want to delete all permissions for this role?")) {
      try {
        await axios.put(`${BASE_URL}/role-master/update/${selectedRoles.role}`, { permissions: [] });
        alert("Permissions deleted successfully.");
        handleReset();
      } catch (error) {
        console.error('Error deleting permissions:', error);
        alert("Error deleting permissions.");
      }
    }
  };

  return (
    <>
      {/* outer wrapper text color */}
      <div style={{ padding: '20px', color: mode === 'dark' ? '#fff' : 'inherit' }}>
        <div className="main-wrapper">
          <div className="page-wrapper">
            <Container
              className="content container-fluid"
              sx={{ color: mode === 'dark' ? '#fff' : 'inherit' }}
            >
              {/* Header and Breadcrumb */}
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ my: 2 }}>
                <div>
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontWeight: 700,
                      color: mode === 'dark' ? '#fff' : 'inherit'
                    }}
                  >
                    Permission Master
                  </Typography>
                  <ul className="breadcrumb" style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                    <li className="breadcrumb-item">
                      <Link
                        to="/home"
                        style={{ color: mode === 'dark' ? '#fff' : 'inherit', textDecoration: 'none' }}
                      >
                        Dashboard
                      </Link>
                    </li>
                    <li
                      className="breadcrumb-item active"
                      style={{ color: mode === 'dark' ? '#fff' : 'inherit' }}
                    >
                      Permissions
                    </li>
                  </ul>
                </div>
              </Stack>

              <Paper sx={{ p: 2, m: 2 }}>
                <form onSubmit={handleSubmitPermissions}>
                  <Stack spacing={3} sx={{ p: 3 }}>
                    {/* Role Selection */}
                    <FormControl sx={styles.formControl} fullWidth size="small">
                      <InputLabel id="role-label">Select Role</InputLabel>
                      <Select
                        labelId="role-label"
                        id="role"
                        value={selectedRoles.role}
                        label="Select Role"
                        onChange={handleRoleChange('role')}
                      >
                        <MenuItem value="">
                          <em>Select Role</em>
                        </MenuItem>
                        {roles.map((role) => (
                          <MenuItem key={role._id} value={role._id}>
                            {role.role}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Parent Selection */}
                    <FormControl sx={styles.formControl} fullWidth size="small">
                      <InputLabel id="parent-label">Select Parent</InputLabel>
                      <Select
                        labelId="parent-label"
                        id="parent"
                        value={selectedRoles.parent}
                        label="Select Parent"
                        onChange={handleRoleChange('parent')}
                      >
                        <MenuItem value="">
                          <em>Select Parent</em>
                        </MenuItem>
                        {data.map((item) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.id}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Child Selection */}
                    <FormControl sx={styles.formControl} fullWidth size="small">
                      <InputLabel id="child-label">Select Child</InputLabel>
                      <Select
                        labelId="child-label"
                        id="child"
                        value={selectedRoles.child}
                        label="Select Child"
                        onChange={handleRoleChange('child')}
                      >
                        <MenuItem value="">
                          <em>Select Child</em>
                        </MenuItem>
                        {dataa.map((item) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.id}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Permissions Table */}
                    <TableContainer component={Paper} sx={{ ...(mode === 'dark' && { bgcolor: '#2d3448' }) }}>
                      <Table sx={{ width: '100%' }}>
                        <TableHead>
                          <TableRow sx={{ ...(mode === 'dark' && { bgcolor: '#2d3448' }) }}>
                            {['Label', 'Read', 'Write', 'Update', 'Print'].map((head) => (
                              <TableCell
                                key={head}
                                sx={{
                                  color: mode === 'dark' ? '#fff' : 'inherit',
                                  fontWeight: 'bold'
                                }}
                              >
                                {head}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {tabledata.map((action) => (
                            <TableRow
                              key={action.id}
                              sx={{ ...(mode === 'dark' && { bgcolor: '#2d3448' }) }}
                            >
                              <TableCell sx={{ color: mode === 'dark' ? '#fff' : 'inherit' }}>
                                {action.id.toUpperCase()}
                              </TableCell>
                              {['read', 'write', 'update', 'print'].map((perm) => (
                                <TableCell key={perm}>
                                  <Checkbox
                                    checked={
                                      permissions[
                                      `${selectedRoles.parent}-${selectedRoles.child}-${action.id}${perm.charAt(0).toUpperCase() + perm.slice(1)}`
                                      ] || false
                                    }
                                    onChange={() => handleCheckboxChange(action.id, perm)}
                                    sx={{
                                      color: mode === 'dark' ? '#fff' : undefined,
                                      '&.Mui-checked': {
                                        color: mode === 'dark' ? '#fff' : undefined
                                      }
                                    }}
                                  />
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* Action Buttons */}
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                      <Button variant="outlined" onClick={handleReset}>
                        Reset
                      </Button>
                      <Button type="submit" variant="contained" sx={{
                        backgroundColor: '#2d9cdb', // override default bg
                        color: '#ffffff',

                        '&:hover': {
                          backgroundColor: '#0b1d51 !important',
                          color: 'white',
                        },
                      }}>
                        Update
                      </Button>
                      <Button variant="contained" color="error" onClick={handleDeletePermissions}>
                        Delete
                      </Button>
                    </Stack>
                  </Stack>
                </form>
              </Paper>
            </Container>
          </div>
        </div>
      </div>
    </>
  );
};
